import { Component, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { PaymentHistoryFilter } from '../../../../core/filters/payment-history.filter';
import { PaymentStatus } from '../../../../core/enums/data.enums';
import { ParentFocusDirective } from '../../../../core/directives/parent-focus.directive';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { MultiSelectComponent } from '../../../../parts/multi-select/multi-select.component';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';
import { DatePlaceholderDirective } from '../../../../core/directives/date-placeholder.directive';

@Component({
  selector: 'ms-payment-history-search-filter',
  imports: [CommonModule, FormsModule, FaDuotoneIconComponent, ParentFocusDirective, MultiSelectComponent, DatePlaceholderDirective],
  templateUrl: './payment-history-search-filter.html',
  styleUrl: './payment-history-search-filter.scss'
})
export class PaymentHistorySearchFilter extends BaseSearchFilterComponent<PaymentHistoryFilter> implements OnInit {
  public filter = input.required<PaymentHistoryFilter>();

  public selectStatusOptions = [
    PaymentStatus.Pending,
    PaymentStatus.Succeeded,
    PaymentStatus.Marked,
    PaymentStatus.Unmarked,
    PaymentStatus.Failed,
    PaymentStatus.Canceling,
    PaymentStatus.Canceled,
    PaymentStatus.RefundPending,
    PaymentStatus.Refunded
  ];

  // Text inputs
  public propertyTitleInput = '';
  public addressInput = '';
  public lotNumberInput = '';

  // Amount/fee display values (major units, e.g. dollars)
  public minimumAmountDisplay: number | undefined = undefined;
  public maximumAmountDisplay: number | undefined = undefined;
  public minimumFeesDisplay: number | undefined = undefined;
  public maximumFeesDisplay: number | undefined = undefined;

  public selectedStatuses: PaymentStatus[] = [];
  public manualOnlyToggle: 'all' | 'manual' | 'stripe' = 'all';

  constructor(private userSessionService: UserSessionService) {
    super();
  }

  public ngOnInit(): void {
    this.selectedStatuses = [...(this.filter().statuses || [])];
    this.initializeAmountDisplayValues();
    this.initializeManualOnlyToggle();
    this.setFilterCount();
  }

  public override get activeFilter(): PaymentHistoryFilter {
    return this.filter();
  }

  protected override getDefaultFilter(): PaymentHistoryFilter {
    return ListConstants.PAYMENT_HISTORY_FILTER_DEFAULT();
  }

  protected override get preservedFilterKeys(): (keyof PaymentHistoryFilter)[] {
    return ['organizationIds', 'propertyIds', 'memberIds', 'searchTerm'];
  }

  protected override get dropdownSelector(): string {
    return '#payment-history-search-filter-dropdown';
  }

  protected override beforeApply(): void {
    this.convertDisplayValuesToMinorUnits();
    this.applyManualOnlyToggle();
  }

  protected override syncStateFromFilter(): void {
    this.selectedStatuses = [...(this.filter().statuses || [])];
    this.initializeAmountDisplayValues();
    this.initializeManualOnlyToggle();
  }

  private initializeAmountDisplayValues(): void {
    const filter = this.filter();
    const minorUnits = this.userSessionService.currentMinorUnits();
    this.minimumAmountDisplay = filter.minimumAmount != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.minimumAmount, minorUnits) : undefined;
    this.maximumAmountDisplay = filter.maximumAmount != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.maximumAmount, minorUnits) : undefined;
    this.minimumFeesDisplay = filter.minimumFees != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.minimumFees, minorUnits) : undefined;
    this.maximumFeesDisplay = filter.maximumFees != null ? CurrencyUtilities.convertMinorUnitsToMajor(filter.maximumFees, minorUnits) : undefined;
  }

  private initializeManualOnlyToggle(): void {
    const value = this.filter().isManualPayment;
    this.manualOnlyToggle = value === true ? 'manual' : value === false ? 'stripe' : 'all';
  }

  private convertDisplayValuesToMinorUnits(): void {
    const filter = this.filter();
    const minorUnits = this.userSessionService.currentMinorUnits();
    filter.minimumAmount = this.minimumAmountDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.minimumAmountDisplay), minorUnits) : undefined;
    filter.maximumAmount = this.maximumAmountDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.maximumAmountDisplay), minorUnits) : undefined;
    filter.minimumFees = this.minimumFeesDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.minimumFeesDisplay), minorUnits) : undefined;
    filter.maximumFees = this.maximumFeesDisplay != undefined ? CurrencyUtilities.convertMajorUnitsToMinor(Number(this.maximumFeesDisplay), minorUnits) : undefined;
  }

  private applyManualOnlyToggle(): void {
    const filter = this.filter();
    filter.isManualPayment =
      this.manualOnlyToggle === 'manual' ? true :
      this.manualOnlyToggle === 'stripe' ? false :
      undefined;
  }

  public override setFilterCount(): void {
    const filter = this.filter();
    this.filterCount =
      (filter.propertyTitles?.length || 0) +
      (filter.primaryAddresses?.length || 0) +
      (filter.lotNumbers?.length || 0) +
      (filter.statuses?.length || 0) +
      (filter.minimumDueDate ? 1 : 0) +
      (filter.maximumDueDate ? 1 : 0) +
      (filter.minimumPaidDate ? 1 : 0) +
      (filter.maximumPaidDate ? 1 : 0) +
      (filter.minimumAmount != null ? 1 : 0) +
      (filter.maximumAmount != null ? 1 : 0) +
      (filter.minimumFees != null ? 1 : 0) +
      (filter.maximumFees != null ? 1 : 0) +
      (filter.isManualPayment !== undefined ? 1 : 0);
  }

  public addPropertyTitle(): void {
    if (this.propertyTitleInput && this.propertyTitleInput.trim() !== '') {
      this.filter().propertyTitles?.push(this.propertyTitleInput.trim());
    }
    this.propertyTitleInput = '';
  }

  public removePropertyTitle(index: number): void {
    this.filter().propertyTitles?.splice(index, 1);
  }

  public addAddress(): void {
    if (this.addressInput && this.addressInput.trim() !== '') {
      this.filter().primaryAddresses?.push(this.addressInput.trim());
    }
    this.addressInput = '';
  }

  public removeAddress(index: number): void {
    this.filter().primaryAddresses?.splice(index, 1);
  }

  public addLotNumber(): void {
    if (this.lotNumberInput && this.lotNumberInput.trim() !== '') {
      this.filter().lotNumbers?.push(this.lotNumberInput.trim());
    }
    this.lotNumberInput = '';
  }

  public removeLotNumber(index: number): void {
    this.filter().lotNumbers?.splice(index, 1);
  }

  public onStatusChange(event: PaymentStatus[]): void {
    this.filter().statuses = event;
  }
}
